import React, {useState} from 'react'
import { Motion, MotionVariant } from '@ds/motion'
import {
  Link,
  Gear,
  CloudUpload,
  Folder,
  ProcessSpinner
} from '@ds/icons'
import { SideNav, InkDocuSignTheme, ToastMessage, Modal, Button } from "@ds/ui"
import { Theme } from '@ds/ui'
import { PropsWithChildren } from 'react'
import FolderPathModal from './FolderPathModal'

interface SideNavBarProps extends PropsWithChildren {
  currentItem?: number,
  expanded?: boolean
}

export function SideNavBar({
  currentItem = 0,
  expanded = false,
  children,
}: SideNavBarProps) {
  const [selectedIndex, setSelectedIndex] = useState<number>(currentItem)
  const [lockExpanded, setLockExpanded] = useState<boolean>(expanded)
  const [showFolderPathModal, setShowFolderPathModal] = useState(false);
  const [openFolderName, setOpenFolderName] = useState<'Config' | 'SAInstallation' | 'SALog' | 'IISLog'>('Config');
  const [isLoading, setIsLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastKind, setToastKind] = useState<'success' | 'danger'>('success');
  const [toastText, setToastText] = useState<string>('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const showToastMessage = (message: string, kind: 'success' | 'danger', duration: number = 3000) => {
    setToastText(message);
    setToastKind(kind);
    setShowToast(true);
    
    // Automatically hide toast after 3 seconds
    setTimeout(() => {
      setShowToast(false);
    }, duration);
  };

  const handleBackup = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('Admin/Home/BackupFolders', {
        method: 'Get',
      });
      const data = await response.json();
      if (data.success) {
        showToastMessage(`Successfully backed up the folder in ${data.backupPath}`, 'success', 30000); // extend the duration to 30s to give users enough time to copy paste the path. 
      } else {
        showToastMessage(data.error, 'danger', 30000);
      }
    } catch (error) {
      console.error('Error fetching backup folders:', error);
      showToastMessage('Error backing up the folder, Please try backing up the folder manually.', 'danger');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClick = async (index: number) => {
    setSelectedIndex(index)
    
    const folderNames: ('Config' | 'SAInstallation' | 'SALog' | 'IISLog')[] = [
      'Config',
      'SAInstallation',
      'SALog',
      'IISLog'
    ]

    if (index >= 0 && index <= 3) {
      setShowFolderPathModal(true)
      setOpenFolderName(folderNames[index])
    } else if (index === 4) {
      setShowConfirmModal(true);
    }
  }

  React.useEffect(() => {
    setLockExpanded(expanded)
  }, [expanded])

  return (
    <Theme docuSignTheme={InkDocuSignTheme}>
      <div style={{ position: 'relative', height: '100%' }}>
        <div style={{ zIndex: 1000000 }}>
          {showToast && (
            <ToastMessage
              kind={toastKind}
              visible={showToast}
              closeButton={<ToastMessage.Close onClick={() => {setShowToast(false)}} />}
            >
              {toastText}
            </ToastMessage>
          )}
        </div>
        {showFolderPathModal && (
          <div style={{ marginTop: '0' }}>
            <FolderPathModal
              folderName={openFolderName}
              isOpen={showFolderPathModal}
              onClose={() => setShowFolderPathModal(false)}
            />
          </div>
        )}

        {showConfirmModal && (
          <Modal
            visible={showConfirmModal}
            closeButton={
              <div data-qa="modal-close">
                  <Modal.Close onClick={() => setShowConfirmModal(false)} />
              </div>
            }
          >
            <Modal.Header title="Confirm Backup" data-qa={'reauth-modal-header'} />
            <Modal.Body>
                    <div data-qa={'reauth-modal-content'}>
                        <p>To proceed with the backup, please stop the 'DSKeyMgr' service via the Services tab in Task Manager. After the backup is complete, remember to restart the 'DSKeyMgr' service.</p>
                    </div>
            </Modal.Body>
            <Modal.Footer
                    primaryButton={
                        <Button
                            size="small"
                            kind="primary"
                            data-qa={'reauth-modal-primary-button'}
                            text={'Confirm'}
                            onClick={async () => {
                              await handleBackup();
                              setShowConfirmModal(false);
                            }}
                        />
                    }
                    secondaryButton={
                        <Button
                            size="small"
                            kind="tertiary"
                            data-qa={'reauth-modal-secondary-button'}
                            text={'Cancel'}
                            onClick={() => setShowConfirmModal(false)}
                        />
                    }
            />
          </Modal>
        )}

        <div className="side-nav-bar"
        style={{ 
          position: 'fixed',
          top: '8.1875rem',
          height: 'calc(100vh - 8.1875rem)',
          transition: 'width 0.3s ease',
          backgroundColor: '#FFFFFF',
          zIndex: 500,
          textAlign: 'start',
        }}
        >
            <SideNav
              expanded={true}
            >
            <SideNav.Group title="TROUBLESHOOTING FILES">
              <SideNav.Item
                text="Configuration Folder"
                startElement={<Folder />}
                onClick={() => handleClick(0)}
                selected={selectedIndex === 0}
              />
                <SideNav.Item
                  text="Installation Folder"
                  startElement={<Folder />}
                  onClick={() => handleClick(1)}
                  selected={selectedIndex === 1}
                />
                <SideNav.Item
                  text="Security Appliance Logs"
                  startElement={<Folder />}
                  onClick={() => handleClick(2)}
                  selected={selectedIndex === 2}
                />
                <SideNav.Item
                  text="IIS Logs"
                  startElement={<Folder />}
                  onClick={() => handleClick(3)}
                  selected={selectedIndex === 3}
                />
            </SideNav.Group>
            <SideNav.Group title="BACKUP FILES">
            <SideNav.Item
                text="Backup Security Appliance" 
                startElement={
                  <>
                    {isLoading ? (
                      <ProcessSpinner />
                    ) : (
                      <CloudUpload />
                    )}
                  </>
                }
                onClick={() => handleClick(4)}
                selected={selectedIndex === 4}
              />
            </SideNav.Group>
          </SideNav>
        </div>
          {children}
      </div>
    </Theme>
  )
}

export default SideNavBar;
